<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const store = useStore();
const pipelines = useMapGetter('pipelines/getActivePipelines');

const expandedPipelines = ref({});

onMounted(() => {
  if (!pipelines.value || pipelines.value.length === 0) {
    store.dispatch('pipelines/get');
  }
});

const toggleExpand = pipelineId => {
  expandedPipelines.value[pipelineId] = !expandedPipelines.value[pipelineId];
};

const isExpanded = pipelineId => expandedPipelines.value[pipelineId] === true;

// Returns the open (non-terminal) stages for a pipeline
const openStages = pipeline => {
  return (pipeline.stages || []).filter(s => s.stage_type === 'open');
};

// Is the entire pipeline selected (i.e. has an entry in modelValue)?
const isPipelineSelected = pipelineId => {
  return Object.prototype.hasOwnProperty.call(props.modelValue, String(pipelineId));
};

// Is a specific stage selected within a pipeline?
const isStageSelected = (pipelineId, stageId) => {
  const selected = props.modelValue[String(pipelineId)];
  if (!selected) return false;
  if (selected.length === 0) return true; // empty array = all stages
  return selected.map(String).includes(String(stageId));
};

// Are ALL open stages selected for a pipeline?
const allStagesSelected = pipeline => {
  const stages = openStages(pipeline);
  const selected = props.modelValue[String(pipeline.id)];
  if (!selected) return false;
  if (selected.length === 0) return true;
  return stages.every(s => selected.map(String).includes(String(s.id)));
};

// Is the pipeline in indeterminate state (some stages but not all)?
const isPipelineIndeterminate = pipeline => {
  const pKey = String(pipeline.id);
  if (!isPipelineSelected(pKey)) return false;
  const selected = props.modelValue[pKey];
  if (!selected || selected.length === 0) return false;
  const stages = openStages(pipeline);
  const selectedCount = stages.filter(s =>
    selected.map(String).includes(String(s.id))
  ).length;
  return selectedCount > 0 && selectedCount < stages.length;
};

// Toggle entire pipeline on/off
const togglePipeline = pipeline => {
  const newValue = { ...props.modelValue };
  const pKey = String(pipeline.id);

  if (isPipelineSelected(pKey)) {
    delete newValue[pKey];
  } else {
    // Select pipeline with empty array = all stages
    newValue[pKey] = [];
  }

  emit('update:modelValue', newValue);
};

// Toggle a single stage within a pipeline
const toggleStage = (pipeline, stageId) => {
  const newValue = { ...props.modelValue };
  const pKey = String(pipeline.id);
  const stages = openStages(pipeline);

  if (!isPipelineSelected(pKey)) {
    // Pipeline not selected → select pipeline with just this stage
    newValue[pKey] = [String(stageId)];
  } else {
    let selected = [...(newValue[pKey] || [])].map(String);

    // If empty (all stages selected), expand to explicit list then remove
    if (selected.length === 0) {
      selected = stages.map(s => String(s.id));
    }

    const idx = selected.indexOf(String(stageId));
    if (idx >= 0) {
      selected.splice(idx, 1);
      if (selected.length === 0) {
        // No stages left → deselect pipeline entirely
        delete newValue[pKey];
        emit('update:modelValue', newValue);
        return;
      }
    } else {
      selected.push(String(stageId));
    }

    // If all stages are now selected, use empty array shorthand
    if (selected.length === stages.length) {
      newValue[pKey] = [];
    } else {
      newValue[pKey] = selected;
    }
  }

  emit('update:modelValue', newValue);
};

const selectedCount = computed(() => {
  return Object.keys(props.modelValue).length;
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Summary -->
    <p class="text-xs text-n-slate-11 mb-0">
      {{
        selectedCount === 0
          ? t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.PIPELINES.ALL_MONITORED')
          : t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.PIPELINES.COUNT', {
              count: selectedCount,
            })
      }}
    </p>

    <!-- Pipeline list -->
    <div
      v-if="pipelines && pipelines.length > 0"
      class="flex flex-col gap-1"
      :class="{ 'opacity-50 pointer-events-none': disabled }"
    >
      <div
        v-for="pipeline in pipelines"
        :key="pipeline.id"
        class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
      >
        <!-- Pipeline header -->
        <div
          class="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-n-alpha-black2 transition-colors"
          @click="toggleExpand(pipeline.id)"
        >
          <Checkbox
            :model-value="allStagesSelected(pipeline) || isPipelineSelected(pipeline.id)"
            :indeterminate="isPipelineIndeterminate(pipeline)"
            @click.stop
            @change="togglePipeline(pipeline)"
          />
          <div class="flex-1 min-w-0">
            <span class="text-sm font-medium text-n-slate-12">
              {{ pipeline.name }}
            </span>
            <span class="text-xs text-n-slate-10 ml-2">
              {{ openStages(pipeline).length }}
              {{ t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.PIPELINES.STAGES') }}
            </span>
          </div>
          <Icon
            :icon="isExpanded(pipeline.id) ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
            class="size-4 text-n-slate-10 flex-shrink-0"
          />
        </div>

        <!-- Stages (collapsible) -->
        <div
          v-if="isExpanded(pipeline.id)"
          class="border-t border-n-weak px-4 py-2"
        >
          <div
            v-for="stage in openStages(pipeline)"
            :key="stage.id"
            class="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-n-alpha-black2 cursor-pointer transition-colors"
            @click="toggleStage(pipeline, stage.id)"
          >
            <Checkbox
              :model-value="isStageSelected(pipeline.id, stage.id)"
              @click.stop
              @change="toggleStage(pipeline, stage.id)"
            />
            <span
              v-if="stage.color"
              class="w-2.5 h-2.5 rounded-full flex-shrink-0"
              :style="{ backgroundColor: stage.color }"
            />
            <span class="text-sm text-n-slate-12">
              {{ stage.name }}
            </span>
          </div>

          <p
            v-if="openStages(pipeline).length === 0"
            class="text-xs text-n-slate-10 py-2 mb-0"
          >
            {{ t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.PIPELINES.NO_STAGES') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Loading / empty state -->
    <div
      v-else
      class="flex items-center justify-center py-8 text-sm text-n-slate-10"
    >
      {{ t('CAPTAIN_SETTINGS.INSIGHTS_CONFIG.PIPELINES.LOADING') }}
    </div>
  </div>
</template>
